import { useMemo, type ReactNode } from 'react';
import { Badge, Button, Empty, IconButton } from '@viquard/doc-ui/components';
import { IcnPlus } from '@viquard/doc-ui/icons';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import {
  addDays,
  formatDayLong,
  isoDate,
  localDateKey,
  parseISODate,
  startOfWeek,
  todayPlain,
  weekRangeLabel,
} from '../utils/dateUtils.js';
import { CalendarHeader } from '../components/CalendarHeader.js';
import { DayStrip } from '../components/DayStrip.js';
import { EventCard } from '../components/EventCard.js';
import { useSwipe } from '../hooks/useSwipe.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import { useCalendarMessages } from '../i18n.js';

export interface WeekViewMobileProps {
  readonly cursor: string;
  readonly onCursorChange: (next: string) => void;
  readonly selectedDate: string;
  readonly onSelectedDateChange: (next: string) => void;
  readonly today: string;
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly locale?: string | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;
  /** Same role as on MonthViewMobile — consumer-injected domain
   *  content (AvailabilityForDate, booking cards, …) keyed by the
   *  currently-selected day, rendered below the day-detail card. */
  readonly renderDayRailExtras?: ((dateKey: string) => ReactNode) | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  readonly selectedEventKey?: string | null | undefined;
  /** Same role as on MonthViewMobile — render an in-line "add on this
   *  day" affordance so the user doesn't have to thumb-walk back to
   *  the corner FAB and re-pick the date in the composer. */
  readonly onCreateEvent?:
    | ((draft?: { readonly startsAt: string }) => void)
    | undefined;
}

/** Phone-shaped week view: a 7-day pill strip + the selected day's
 *  events as a flat scrollable list. Drops the 7-column timed grid
 *  entirely — that's a desktop-screen pattern that doesn't survive a
 *  phone viewport. */
export function WeekViewMobile(props: WeekViewMobileProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    cursor,
    onCursorChange,
    selectedDate,
    onSelectedDateChange,
    today,
    events,
    kindTheme,
    groups,
    locale,
    weekStartsOn = 0,
    viewerTz,
    onSelectEvent,
    onSelectLink,
    renderDayRailExtras,
    renderEventExtras,
    contextMenu,
    selectedEventKey,
    onCreateEvent,
  } = props;

  const cursorDate = useMemo(() => parseISODate(cursor), [cursor]);
  const weekStart = useMemo(
    () => startOfWeek(cursorDate, weekStartsOn),
    [cursorDate, weekStartsOn],
  );
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const eventCounts = useMemo(() => {
    const map = new Map<string, number>();
    events.forEach((e) => {
      const key = localDateKey(e.startsAt, viewerTz);
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [events, viewerTz]);

  const dayEvents = useMemo(
    () =>
      events
        .filter((e) => localDateKey(e.startsAt, viewerTz) === selectedDate)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [events, selectedDate, viewerTz],
  );

  const goPrev = (): void => {
    const next = isoDate(addDays(cursorDate, -7));
    onCursorChange(next);
    onSelectedDateChange(next);
  };
  const goNext = (): void => {
    const next = isoDate(addDays(cursorDate, 7));
    onCursorChange(next);
    onSelectedDateChange(next);
  };

  // DayStrip has its own horizontal scroll; ignore swipes that started
  // inside it so the user can scrub the strip without changing weeks.
  const swipe = useSwipe({
    onLeft: goNext,
    onRight: goPrev,
    ignoreSelector: '.vq-cal__day-strip',
  });

  return (
    <div className="vq-cal__split--mobile" {...swipe}>
      <div className="vq-cal__panel vq-cal__panel--week-mobile">
        <CalendarHeader
          label={weekRangeLabel(weekStart, weekEnd, locale)}
          onPrev={goPrev}
          onNext={goNext}
          onToday={() => {
            const t = isoDate(todayPlain(viewerTz));
            onCursorChange(t);
            onSelectedDateChange(t);
          }}
        />
        <DayStrip
          anchor={cursor}
          selected={selectedDate}
          today={today}
          onSelect={onSelectedDateChange}
          weekStartsOn={weekStartsOn}
          locale={locale}
          eventCounts={eventCounts}
        />
      </div>

      <section className="vq-cal__day-detail" aria-label={messages.dayDetailAriaLabel}>
        <header className="vq-cal__day-detail-head">
          <div className="vq-cal__day-detail-headline">
            <h3 className="vq-cal__day-detail-title">
              {formatDayLong(parseISODate(selectedDate), locale)}
            </h3>
            {onCreateEvent !== undefined && (
              <IconButton
                label={messages.addEventOnDay}
                variant="ghost"
                size="sm"
                onClick={() => { onCreateEvent({ startsAt: selectedDate }); }}
              >
                <IcnPlus size={16} />
              </IconButton>
            )}
          </div>
          <Badge
            tone="neutral"
            size="md"
            numeric
          >
            {dayEvents.length}
          </Badge>
        </header>
        {dayEvents.length === 0 ? (
          <Empty
            size="compact"
            title={messages.nothingScheduled}
            description={
              onCreateEvent !== undefined
                ? messages.addFirstOfDay
                : messages.pickAnotherDay
            }
            {...(onCreateEvent !== undefined
              ? {
                  action: (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft={<IcnPlus size={14} />}
                      onClick={() => { onCreateEvent({ startsAt: selectedDate }); }}
                    >
                      {messages.newEvent}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <div className="vq-cal__card-stack">
            {dayEvents.map((e) => (
              <EventCard
                key={eventInstanceKey(e)}
                event={e}
                kindTheme={kindTheme}
                groups={groups}
                onClick={onSelectEvent}
                onSelectLink={onSelectLink}
                viewerTz={viewerTz}
                extras={renderEventExtras?.(e)}
                selected={selectedEventKey === eventInstanceKey(e)}
                contextMenu={contextMenu?.(e)}
              />
            ))}
          </div>
        )}
      </section>
      {renderDayRailExtras?.(selectedDate)}
    </div>
  );
}
